import React, { useEffect, useCallback, useMemo } from 'react';

import { debounce } from 'lodash';

import { KEYS, PUSH_MODAL_CLOSED } from '../../../../../../constants/Common';
import { AUTH_FUNCTIONAL } from '../../../../../../constants/Metrics';

import AuthService from '../../../../../../services/Api/Auth';
import MetricsService from '../../../../../../services/Api/Metrics';
import PushWebService from '../../../../../../services/Api/PushWeb';
import UserService from '../../../../../../services/Api/User';
import LocalStorage from '../../../../../../services/LocalStorage';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';


import Button, { IGradient } from '../../../Common/Form/Button';
import LoadingSvg from '../../../Common/LoadingSvg';
import { ERRORS } from '../../Constants';
import { actionRedirectAuth, actionRenderPayment, multipleActions, Types as ContainerTypes } from '../../Store/ContainerReducer';
import { IAuthState } from '../../Store/Reducers';
import { IKeyDown, IInputDataEmail, IInputDataPassword, Types as SignUpTypes } from '../../Store/SignUpReducer';

interface ILoginButton {
    amount: string,
    loading: boolean,
    keyDown: IKeyDown,
    email: IInputDataEmail,
    buttonGradient?: IGradient,
    buttonLoginTextText: string,
    password: IInputDataPassword,
}

type TLoginButton = IConnectedProps<ILoginButton>;

const LoginButton: React.FC<TLoginButton> = ({ intl, dispatch, ...props }) => {
    const {
        email,
        amount,
        loading,
        keyDown,
        password,
        buttonGradient,
        buttonLoginTextText
    } = props;

    const buttonTitle = useMemo(() => buttonLoginTextText || intl.formatMessage({ id: 'authFunctional.login' }), [intl, buttonLoginTextText]);

    const disabled = useMemo(() => (!email.value || !password.value) && !(email.error && password.error), [email.error, email.value, password.error, password.value]);

    const handleError = useCallback((status = '400') => {
        const errorName = (ERRORS as Record<string, string>)[status];

        const actions = [
            {
                type: SignUpTypes.CHANGE_LOGIN_LOADING,
                data: false
            },
            {
                type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                field: 'email',
                data: { error: intl.formatMessage({ id: `authFunctional.errors.${errorName}` }) }
            }
        ];

        if (!status) {
            actions.push({
                type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                field: 'password',
                data: { error: intl.formatMessage({ id: `authFunctional.errors.${errorName}` }) }
            });
        }

        multipleActions(dispatch, actions);
    }, [intl, dispatch]);

    const handleLinkToken = async () => {
        try {
            const $PushWebService = new PushWebService();

            const modalClosedBefore = window.localStorage.getItem(PUSH_MODAL_CLOSED);

            if ($PushWebService.getIsNotificationGranted()) {
                $PushWebService.handleLinkToken();
            }

            if (!$PushWebService.getIsNotificationGranted() && modalClosedBefore) {
                LocalStorage.removeItem(PUSH_MODAL_CLOSED);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleClickButon = useCallback(async () => {
        const $AuthService = new AuthService();
        const $UserService = new UserService();
        const $MetricsService = new MetricsService();

        if (!(email.value && password.value)) return;

        multipleActions(dispatch, [
            {
                type: SignUpTypes.CHANGE_LOGIN_LOADING,
                data: true
            },
            { type: ContainerTypes.SHOW_PAYMENT_MODAL }
        ]);

        try {
            await handleLinkToken();
        } catch(error) {
            console.error(error);
        }

        try {
            const res = await $AuthService.signIn(email.value, password.value);
            if (!res) {
                handleError();
                return;
            }

            $MetricsService.setMetricsWithJWT(res.astToken, AUTH_FUNCTIONAL.FEATURE, window.location.pathname, AUTH_FUNCTIONAL.ACTION_LOGIN, '');

            $UserService.token = res.astToken;
            const { user } = await $UserService.getUser();

            const isSubscriber = user.usu_assinante === 'S';

            if (!amount || isSubscriber) {
                actionRedirectAuth(res.astToken);
                return;
            }

            actionRenderPayment(dispatch, res.astToken);
        } catch (error: any) {
            handleError(error.status);
        }
    }, [amount, email.value, password.value, dispatch, handleError]);

    const debounceClick = useCallback(() => debounce(() => handleClickButon(), 1000), [handleClickButon]);

    useEffect(() => {
        if (!keyDown.updatedAt) return;

        debounceClick();
    }, [debounceClick, keyDown.updatedAt]);

    return (
        <Button
            loading={loading}
            disabled={disabled}
            variation="gradient"
            id="auth-login-button"
            gradient={buttonGradient}
            onClick={handleClickButon}
        >
            {loading ? <LoadingSvg /> : <span>{buttonTitle}</span>}
        </Button>
    );
};

const mapStateToProps: IAuthMapStateToProps<ILoginButton> = (state, isComparison = false) => ((!isComparison)
    ? {
        amount: (state as IAuthState).Container.pagarme.amount,
        email: (state as IAuthState).SignUp.personalData.email,
        loading: (state as IAuthState).SignUp.loginButtonLoading,
        password: (state as IAuthState).SignUp.personalData.password,
        buttonGradient: (state as IAuthState).Container.buttonGradient,
        buttonLoginTextText: (state as IAuthState).Container.communication.buttonLoginText,
        keyDown: (KEYS.ENTER === (state as IAuthState).SignUp.keyDown.key) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        email: state.email,
        amount: state.amount,
        keyDown: state.keyDown,
        loading: state.loading,
        password: state.password,
        buttonGradient: state.buttonGradient,
        buttonLoginTextText: state.buttonLoginTextText
    }
);

export default connectAuth(mapStateToProps)(LoginButton);
